/**
 * Cross-sell components. Feature docs: .claude/features/feature-cross-sell/.
 *
 * <cross-sell-list>  Product page "Complete the set" row (snippets/cross-sell-product.liquid).
 * <cross-sell-cart>  Cart "Add to your order" slot (snippets/cross-sell-cart.liquid), in the
 *                    cart drawer and on the cart page.
 *
 * Both are server-rendered; nothing is fetched on page load. The behavior here is the
 * accessibility-lead's requirements (review of 2026-10-08), handled without editing the base
 * files (product-form.js, cart-items.js, quick-add.js, main.js):
 * - After an add from the cart slot, focus moves to the slot heading, or to the added line
 *   item when no suggestion is left, and "[title] added to cart" is announced.
 * - An add error from the cart slot survives the drawer refresh that product-form.js triggers.
 * - Quick-buy opened from a suggestion carries the hidden _source property, and focus is
 *   repaired when quick-buy closes after its opener was re-rendered away.
 */
(() => {
  // Shared across instances: the cart drawer re-render replaces the slot element, so the
  // record of what was just added has to outlive it. Entries expire, so a re-render that never
  // happened cannot leak into a later, unrelated one.
  const pending = {
    add: null, // { variantId, text, at }
    error: null // { productId, message, at }
  };
  const PENDING_TTL_MS = 5000;

  const ANNOUNCER_ID = 'cross-sell-announcer';
  let announceTimer = null;
  let clearTimer = null;

  /**
   * True when a pending entry exists and is recent enough to act on.
   * @param {object|null} entry - pending.add or pending.error.
   * @returns {boolean}
   */
  function isFresh(entry) {
    return !!entry && Date.now() - entry.at < PENDING_TTL_MS;
  }

  /**
   * Returns the persistent, body-level polite live region, creating it if needed. Both
   * components call this when they connect, so the region is in the page well before its
   * first message: screen readers often miss a region that is created and filled at once.
   * @returns {HTMLElement}
   */
  function getAnnouncer() {
    let region = document.getElementById(ANNOUNCER_ID);
    if (!region) {
      region = document.createElement('div');
      region.id = ANNOUNCER_ID;
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }
    return region;
  }

  /**
   * Announces text through the live region, outside every re-rendered container, so
   * re-renders never cut an announcement short. Clears the text again after a few seconds so
   * a stale message is not left at the end of the page for browse-mode readers.
   * @param {string} text - Text to announce.
   */
  function announce(text) {
    if (!text) return;
    const region = getAnnouncer();
    clearTimeout(announceTimer);
    clearTimeout(clearTimer);
    region.textContent = '';
    announceTimer = setTimeout(() => {
      region.textContent = text;
      clearTimer = setTimeout(() => {
        region.textContent = '';
      }, 5000);
    }, 100);
  }

  /**
   * Builds the "[title] added to cart" message from the element's translated template.
   * @param {Element} el - Element carrying data-added-text.
   * @param {string} title - Product title.
   * @returns {string}
   */
  function addedText(el, title) {
    const template = el.dataset.addedText || '';
    return template.replace('[title]', title);
  }

  /**
   * True when focus has been lost: nothing, the body, an element no longer in the page, or an
   * element inside hidden content (a closed drawer keeps focus in Firefox and Safari).
   * @returns {boolean}
   */
  function focusIsLost() {
    const active = document.activeElement;
    return !active || active === document.body || !active.isConnected
      || !!active.closest('[aria-hidden="true"]');
  }

  /**
   * Reads the product title and id from the card that contains an element.
   * @param {Element} el - Element inside a cross-sell card.
   * @returns {{title: string, productId: string}|null}
   */
  function cardInfo(el) {
    const card = el.closest('[data-product-id]');
    if (!card) return null;
    const link = card.querySelector('.card-link');
    return {
      title: link ? link.textContent.trim() : '',
      productId: card.dataset.productId
    };
  }

  /**
   * Makes the quick-buy add carry the hidden _source property when quick-buy was opened from
   * a suggestion. A capture-phase submit listener on the quick-buy drawer runs before
   * product-form.js reads the form. Removed when quick-buy closes.
   * @param {HTMLElement} button - The suggestion's .js-quick-add button.
   */
  function armQuickBuySource(button) {
    const drawer = document.querySelector('quick-add-drawer');
    const { crossSellSource: source, productUrl: url } = button.dataset;
    if (!drawer || !source || !url) return;

    const onSubmit = (evt) => {
      const form = evt.target;
      if (!(form instanceof HTMLFormElement) || drawer.productUrl !== url) return;
      let input = form.querySelector('input[name="properties[_source]"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_source]';
        form.appendChild(input);
      }
      input.value = source;
    };

    const cleanup = () => {
      drawer.removeEventListener('submit', onSubmit, true);
      document.removeEventListener('on:quickbuy:after-close', cleanup);
    };

    drawer.addEventListener('submit', onSubmit, true);
    document.addEventListener('on:quickbuy:after-close', cleanup);
  }

  if (!customElements.get('cross-sell-list')) {
    customElements.define(
      'cross-sell-list',
      class CrossSellList extends HTMLElement {
        connectedCallback() {
          this.clickHandler = this.handleClick.bind(this);
          this.addHandler = this.handleAdd.bind(this);
          this.addEventListener('click', this.clickHandler);
          this.addEventListener('on:cart:add', this.addHandler);
          this.addEventListener('on:line-item:change', this.addHandler);
          getAnnouncer();
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.clickHandler);
          this.removeEventListener('on:cart:add', this.addHandler);
          this.removeEventListener('on:line-item:change', this.addHandler);
          if (this.drawerCloseHandler) {
            document.removeEventListener('on:cart-drawer:after-close', this.drawerCloseHandler);
          }
        }

        /**
         * Arms the _source property for quick-buy opened from this row.
         * @param {Event} evt - Click event.
         */
        handleClick(evt) {
          const button = evt.target.closest('.js-quick-add');
          if (button && this.contains(button)) armQuickBuySource(button);
        }

        /**
         * After a one-tap add from this row: announce it once the cart drawer has opened, and
         * return focus to the button when the drawer closes (the theme opens the drawer
         * without an opener, so it cannot do this itself).
         * @param {CustomEvent} evt - on:cart:add or on:line-item:change from a card's form.
         */
        handleAdd(evt) {
          const button = evt.target.querySelector('[name="add"]');
          const info = cardInfo(evt.target);
          if (!info) return;

          // The drawer opens about 700ms after the add (product-form.js); speak after that,
          // so the message does not talk over the dialog's name.
          setTimeout(() => announce(addedText(this, info.title)), 1000);

          if (!button || !document.querySelector('cart-drawer')) return;
          if (this.drawerCloseHandler) {
            document.removeEventListener('on:cart-drawer:after-close', this.drawerCloseHandler);
          }
          this.drawerCloseHandler = () => {
            document.removeEventListener('on:cart-drawer:after-close', this.drawerCloseHandler);
            this.drawerCloseHandler = null;
            if (button.isConnected && focusIsLost()) button.focus();
          };
          document.addEventListener('on:cart-drawer:after-close', this.drawerCloseHandler);
        }
      }
    );
  }

  if (!customElements.get('cross-sell-cart')) {
    customElements.define(
      'cross-sell-cart',
      class CrossSellCart extends HTMLElement {
        connectedCallback() {
          this.clickHandler = this.handleClick.bind(this);
          this.addHandler = this.handleAdd.bind(this);
          this.errorHandler = this.handleError.bind(this);
          this.addEventListener('click', this.clickHandler);
          this.addEventListener('on:cart:add', this.addHandler);
          this.addEventListener('on:line-item:change', this.addHandler);
          this.addEventListener('on:cart:error', this.errorHandler);
          getAnnouncer();

          // A fresh slot after the cart drawer re-rendered: finish what the old one started.
          if (this.closest('cart-drawer')) this.resolvePending();
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.clickHandler);
          this.removeEventListener('on:cart:add', this.addHandler);
          this.removeEventListener('on:line-item:change', this.addHandler);
          this.removeEventListener('on:cart:error', this.errorHandler);
          if (this.abortController) this.abortController.abort();
        }

        /**
         * Re-renders the slot through the Section Rendering API. Called by
         * CartItems.updateRecommendations() (cart-items.js) after quantity changes, and after
         * an add on the cart page. Fetched from the current path so request.page_type, and so
         * the heading level, matches the page.
         */
        async init() {
          if (this.abortController) this.abortController.abort();
          const controller = new AbortController();
          this.abortController = controller;

          const focusedId = this.contains(document.activeElement)
            ? document.activeElement.closest('[data-product-id]')?.dataset.productId
            : null;
          const hadFocus = this.contains(document.activeElement);

          this.setAttribute('aria-busy', 'true');
          try {
            const response = await fetch(`${window.location.pathname}?section_id=cross-sell-cart`, {
              signal: controller.signal
            });
            if (!response.ok) throw new Error(response.status);

            const tmpl = document.createElement('template');
            tmpl.innerHTML = await response.text();
            const fresh = tmpl.content.querySelector('cross-sell-cart');
            this.innerHTML = fresh ? fresh.innerHTML : '';

            if (hadFocus) this.restoreFocus(focusedId);
            this.resolvePending();
          } catch (error) {
            if (error.name !== 'AbortError') console.log(error); // eslint-disable-line
          } finally {
            // A newer request may still be loading; only it clears the busy state.
            if (this.abortController === controller) this.removeAttribute('aria-busy');
          }
        }

        /**
         * After a re-render that replaced the focused element, put focus back on the same
         * product's button, otherwise on the heading.
         * @param {string|null} productId - Product id of the card that had focus.
         */
        restoreFocus(productId) {
          if (!focusIsLost()) return;
          const card = productId ? this.querySelector(`[data-product-id="${productId}"]`) : null;
          const target = card?.querySelector('[name="add"], .js-quick-add, a.btn')
            || this.querySelector('.cross-sell__heading');
          if (target) target.focus();
        }

        /**
         * Arms the _source property and the focus repair for quick-buy opened from the slot.
         * @param {Event} evt - Click event.
         */
        handleClick(evt) {
          const button = evt.target.closest('.js-quick-add');
          if (!button || !this.contains(button)) return;
          armQuickBuySource(button);

          const onCart = window.location.pathname === theme.routes.cart;
          const repair = () => {
            document.removeEventListener('on:quickbuy:after-close', repair);
            // The drawer has already tried to refocus its opener (SideDrawer.close() does that
            // before this event fires); act only if that failed. setTimeout, not
            // requestAnimationFrame, which never runs in a background tab.
            setTimeout(() => {
              const active = document.activeElement;
              if (!focusIsLost()) return;
              if (onCart) {
                const slot = document.getElementById('cart-recommendations');
                const target = slot?.querySelector('.cross-sell__heading') || document.querySelector('.js-item-name');
                if (target) target.focus();
              } else {
                const openDrawer = document.querySelector('cart-drawer[open]');
                if (openDrawer && openDrawer.contains(active)) return;
                const cartIcon = document.getElementById('cart-icon');
                if (cartIcon) cartIcon.focus();
              }
            }, 0);
          };
          document.addEventListener('on:quickbuy:after-close', repair);
        }

        /**
         * Remembers a successful add from the slot, so the re-rendered slot can move focus and
         * announce it.
         * @param {CustomEvent} evt - on:cart:add or on:line-item:change from a card's form.
         */
        handleAdd(evt) {
          const info = cardInfo(evt.target);
          if (!info) return;
          pending.add = {
            variantId: String(evt.detail?.variantId || ''),
            text: addedText(this, info.title),
            at: Date.now()
          };
          pending.error = null;
        }

        /**
         * Remembers an add error from the slot. product-form.js refreshes the cart drawer
         * straight after, which would wipe the card's own alert.
         * @param {CustomEvent} evt - on:cart:error from a card's form.
         */
        handleError(evt) {
          const info = cardInfo(evt.target);
          if (!info || !this.closest('cart-drawer')) return;
          pending.error = { productId: info.productId, message: evt.detail?.error || '', at: Date.now() };
          pending.add = null;
        }

        /**
         * Applies a remembered add or error to this (freshly rendered) slot.
         */
        resolvePending() {
          if (pending.error) {
            const entry = pending.error;
            pending.error = null;
            if (isFresh(entry)) this.showError(entry.productId, entry.message);
            return;
          }

          if (!isFresh(pending.add)) {
            pending.add = null;
            return;
          }
          const { variantId, text } = pending.add;
          pending.add = null;

          announce(text);
          if (!focusIsLost() && !this.contains(document.activeElement)) return;

          const drawer = this.closest('cart-drawer');
          if (drawer && !drawer.hasAttribute('open')) return;

          const scope = drawer || document;
          const target = this.querySelector('.cross-sell__heading')
            || scope.querySelector(`.cart-item[data-variant-id="${variantId}"] .js-item-name`)
            || scope.querySelector('.js-item-name')
            || drawer;
          if (!target) return;

          if (drawer && typeof trapFocus === 'function') {
            trapFocus(drawer, target);
          } else {
            target.focus();
          }
        }

        /**
         * Shows an add error on the re-rendered card and focuses its button.
         * @param {string} productId - Product id of the card that failed.
         * @param {string} message - Error text from product-form.js.
         */
        showError(productId, message) {
          // On a network failure product-form.js has no message to pass on.
          const text = message || theme.strings.cartError;
          const drawer = this.closest('cart-drawer');
          const canFocus = !drawer || drawer.hasAttribute('open');
          const card = this.querySelector(`[data-product-id="${productId}"]`);
          const alert = card?.querySelector('.js-form-error');
          const button = card?.querySelector('[name="add"]');

          if (alert) {
            alert.textContent = text;
            alert.hidden = false;
            if (button) {
              button.setAttribute('aria-describedby', alert.id);
              // product-form.js hides the alert on retry but keeps its text; drop the
              // description so the retry does not read the old error.
              button.addEventListener('click', () => button.removeAttribute('aria-describedby'), { once: true });
              if (canFocus) button.focus();
            }
            return;
          }

          // The card is gone (for example the product sold out): say so without a card.
          announce(text);
          const target = this.querySelector('.cross-sell__heading') || drawer;
          if (target && canFocus && focusIsLost()) target.focus();
        }
      }
    );
  }
})();
